import { MaybeRefOrGetter } from '@vue/reactivity'
import { useEventBus } from '@vueuse/core'
import { cloneDeep, isEqual } from 'lodash-es'
import { Ref, computed, onUnmounted, ref, toValue } from 'vue'

import { reportInstanceKey } from '@/api/events'
import { useReportStore } from '@/modules/report/stores/ReportStore'

import {
  REPORT_STATUS,
  ReportArgs,
  ReportInstanceRead,
  ReportInstanceWrite,
} from '@generated/types'

export interface UseGenerateReportReturn {
  afterReportDownloaded: () => void
  generateReport: () => Promise<void>
  isGeneratingReport: Ref<boolean>
  reportInCreation: Ref<ReportInstanceRead | null>
  reportInCreationLink: Ref<string>
  reportInCreationStatus: Ref<ReportInCreationStatus>
}

export type ReportInCreationStatus =
  (typeof REPORT_IN_CREATION_STATUS)[keyof typeof REPORT_IN_CREATION_STATUS]
export const REPORT_IN_CREATION_STATUS = {
  NOT_GENERATED: 'not_generated',
  WORKING: REPORT_STATUS.WORKING,
  COMPLETE: REPORT_STATUS.COMPLETE,
  FAILED: REPORT_STATUS.FAILED,
}

export function useGenerateReport(
  report: MaybeRefOrGetter<ReportInstanceWrite['report']>,
  reportArgs: MaybeRefOrGetter<ReportArgs>,
): UseGenerateReportReturn {
  const reportInCreation = ref<ReportInstanceRead | null>(null)
  const reportInCreationStatus = computed<ReportInCreationStatus>(function () {
    if (reportInCreation.value === null) {
      return REPORT_IN_CREATION_STATUS.NOT_GENERATED
    }
    if (reportInCreation.value.status === REPORT_STATUS.COMPLETE) {
      return REPORT_IN_CREATION_STATUS.COMPLETE
    }
    if (reportInCreation.value.status === REPORT_STATUS.FAILED) {
      return REPORT_IN_CREATION_STATUS.FAILED
    }
    return REPORT_IN_CREATION_STATUS.WORKING
  })
  const reportInCreationLink = computed<string>(function () {
    if (!reportInCreation.value) return ''
    return `/api/report/${reportInCreation.value.id}/download/`
  })
  function afterReportDownloaded() {
    setTimeout(() => {
      reportInCreation.value = null
    }, 100)
  }
  const isGeneratingReport = computed<boolean>(function () {
    return (
      reportInCreation.value !== null &&
      (reportInCreation.value.status === REPORT_STATUS.WORKING ||
        reportInCreation.value.status === REPORT_STATUS.PENDING)
    )
  })

  const reportStore = useReportStore()
  async function generateReport() {
    await reportStore.createReport({
      name: 'Report',
      report: toValue(report),
      args: toValue(reportArgs),
    })
  }

  // W E B    S O C K E T    E V E N T S
  const bus = useEventBus(reportInstanceKey)
  function onReportInstanceCreated(reportInstance: ReportInstanceRead) {
    // Otherwise we'll get the same report instance multiple times in all the components that use this composable
    if (!isEqual(reportInstance.args, toValue(reportArgs))) return

    reportStore.storeReport(cloneDeep(reportInstance))
    if (
      reportInCreation.value === null ||
      (reportInCreation.value && reportInCreation.value.id === reportInstance.id)
    ) {
      reportInCreation.value = cloneDeep(reportInstance)
    }
  }
  const unsubscribe = bus.on(onReportInstanceCreated)
  onUnmounted(unsubscribe)

  return {
    afterReportDownloaded,
    generateReport,
    isGeneratingReport,
    reportInCreation,
    reportInCreationLink,
    reportInCreationStatus,
  }
}
